import { useState } from "react";
import { ChevronsUpDown, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { ScopeSelect } from "@/components/itsm-config/scope-select";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import {
  useFreshserviceTicketTypes,
  useJiraIssueTypes,
  useSnowTicketTypes,
} from "@/hooks/use-itsm-metadata";
import { useScopeLabel } from "@/hooks/use-itsm-labels";
import { isEmptyMatch, listScopedRules, sameMatch } from "@/lib/itsm-config/general-mapping";
import type {
  GeneralMappingRule,
  GeneralMatch,
  ItsmProviderFamily,
} from "@/lib/types/itsm-config";

interface Props {
  family: ItsmProviderFamily;
  tenantId: string;
  credentialId: string;
  rules: GeneralMappingRule[];
  /** Does this rule carry the field this card owns? (decides which scoped
   *  exceptions to list). */
  hasField: (rule: GeneralMappingRule) => boolean;
  /** Render the inline editor bound to a given scope match. */
  renderEditor: (match: GeneralMatch) => React.ReactNode;
  /** Clear this card's field for a scope (called on remove-exception). */
  onClearScope?: (match: GeneralMatch) => void;
  /** Copy under the default editor, e.g. "Applies to all projects". */
  defaultNote?: string;
}

/**
 * Default-plus-advanced wrapper for the general_mapping cards. The tenant-wide
 * default (empty match) editor is always shown; an "Advanced" section reveals
 * per-scope exceptions and a picker to add one. Each card supplies its own
 * editor via `renderEditor`, so this component stays field-agnostic.
 */
export function ScopeReveal({
  family,
  tenantId,
  credentialId,
  rules,
  hasField,
  renderEditor,
  onClearScope,
  defaultNote = "Applies to all projects.",
}: Props) {
  // Scopes added this session that don't yet have a persisted rule for this
  // field (so their editor is visible before anything is filled in).
  const [extraMatches, setExtraMatches] = useState<GeneralMatch[]>([]);
  const [newScope, setNewScope] = useState("");
  const [newIssueType, setNewIssueType] = useState("");

  const persistedScoped = listScopedRules(rules).filter(hasField);
  const shownMatches: GeneralMatch[] = [
    ...persistedScoped.map((r) => r.match),
    ...extraMatches.filter(
      (m) => !persistedScoped.some((r) => sameMatch(r.match, m)),
    ),
  ];

  const jiraIssueTypes = useJiraIssueTypes(
    family === "jira" ? tenantId : undefined,
    credentialId,
    family === "jira" ? newScope || undefined : undefined,
  );
  const snowTicketTypes = useSnowTicketTypes(
    family === "servicenow" ? tenantId : undefined,
    credentialId,
  );
  const fsTicketTypes = useFreshserviceTicketTypes(
    family === "freshservice" ? tenantId : undefined,
    credentialId,
  );
  const issueTypeQuery =
    family === "servicenow"
      ? snowTicketTypes
      : family === "freshservice"
        ? fsTicketTypes
        : jiraIssueTypes;

  const addException = () => {
    const match: GeneralMatch = {
      integration_id: null,
      provider_scope: newScope.trim() || null,
      issue_type: newIssueType.trim() || null,
    };
    // Need at least one predicate — an all-empty match is the default rule.
    if (isEmptyMatch(match)) return;
    if (!shownMatches.some((m) => sameMatch(m, match))) {
      setExtraMatches((prev) => [...prev, match]);
    }
    setNewScope("");
    setNewIssueType("");
  };

  const removeException = (match: GeneralMatch) => {
    setExtraMatches((prev) => prev.filter((m) => !sameMatch(m, match)));
    onClearScope?.(match);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        {renderEditor({
          integration_id: null,
          provider_scope: null,
          issue_type: null,
        })}
        <p className="text-muted-foreground text-xs">{defaultNote}</p>
      </div>

      <Collapsible>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2">
            <ChevronsUpDown className="mr-1 h-4 w-4" />
            Advanced: limit to a project / group / issue type
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-2">
          {shownMatches.map((match) => (
            <ScopeExceptionBlock
              key={`${match.provider_scope ?? ""}-${match.issue_type ?? ""}`}
              family={family}
              tenantId={tenantId}
              credentialId={credentialId}
              match={match}
              onRemove={() => { removeException(match); }}
            >
              {renderEditor(match)}
            </ScopeExceptionBlock>
          ))}

          <div className="flex items-end gap-2 border-t pt-3">
            <div className="space-y-1">
              <Label className="text-xs">Scope (optional)</Label>
              <div className="w-[220px]">
                <ScopeSelect
                  family={family}
                  tenantId={tenantId}
                  integrationId={credentialId}
                  value={newScope}
                  onChange={setNewScope}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Issue type (optional)</Label>
              <div className="w-[200px]">
                <MetadataSelect
                  options={issueTypeQuery.data?.options ?? []}
                  isLoading={issueTypeQuery.isLoading}
                  value={newIssueType}
                  onChange={setNewIssueType}
                  placeholder="Any"
                />
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!newScope.trim() && !newIssueType.trim()}
              onClick={addException}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add exception
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/** A labeled block wrapping one scope's editor, with a resolved scope name. */
function ScopeExceptionBlock({
  family,
  tenantId,
  credentialId,
  match,
  onRemove,
  children,
}: {
  family: ItsmProviderFamily;
  tenantId: string;
  credentialId: string;
  match: GeneralMatch;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  const scopeLabel = useScopeLabel(
    family,
    tenantId,
    credentialId,
    match.provider_scope,
  );
  return (
    <div className="space-y-1 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">
          {scopeLabel || (match.issue_type ? "Any project" : "(scope)")}
          {match.issue_type && (
            <span className="text-muted-foreground ml-2 text-xs">
              · {match.issue_type}
            </span>
          )}
        </p>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRemove}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      {children}
    </div>
  );
}
